import { Alert, Modal } from 'antd';
import * as React from 'react';
import APITokenUsersApiService from '../../api/APITokenUsersApiService';
import AddAPITokenUserForm from '../../components/forms/AddAPITokenUserForm';
import Routes from '../../config/Routes';
import FeatureFlag from '../../consts/FeatureFlag';
import TableRequestDTO from '../../models/TableRequestDTO';
import APITokenUserDTO from '../../models/APITokenUserDTO';
import BaseDataTableState from '../../redux/bases/BaseDataTableState';
import AuthorizationUtil from '../../utils/AuthorizationUtil';
import StringUtil from '../../utils/StringUtil';
import TableFilterUtil, { TableFilterOption } from '../../utils/TableFilterUtil';
import DataTable, { DataTableColumnProps, FilterType } from './core/DataTable';
import DataTableButtonUtil from './core/DataTableButtonUtil';
import HistoryUtil from '../../utils/HistoryUtil';

interface APITokenUsersDataTableProps {
  
}

interface APITokenUsersDataTableState extends BaseDataTableState<APITokenUserDTO> {
  roles: TableFilterOption[];
  statuses: TableFilterOption[];
  addAPITokenUser: boolean;
  errors?: string[];
}

class APITokenUsersDataTable extends React.Component<APITokenUsersDataTableProps, APITokenUsersDataTableState> {
  private _dataTable: DataTable<APITokenUserDTO> | undefined;
  private readonly _addAPITokenUserFormRef = React.createRef<AddAPITokenUserForm>();

  constructor(props: APITokenUsersDataTableProps) {
    super(props);

    this.state = {
      roles: [],
      statuses: [],
      addAPITokenUser: false
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    TableFilterUtil.Roles().then(x => this.setState({ roles: x }));

    const loaders = [] as any[];

    Promise.all(loaders).then(() => {
      this.setState({ loading: false });
    });
  }

  private getColumnDefinitions = () => {
    const columns = [
      {
        title: '',
        dataIndex: false,
        width: 25,
      },
      {
        title: 'API Name',
        dataIndex: APITokenUserDTO.apiName,
        sorter: true,
        filterType: FilterType.Text
      },
      {
        title: 'API Key',
        dataIndex: APITokenUserDTO.apiKey,
        sorter: true,
        filterType: FilterType.Text,
        width: 350,
      }
    ] as DataTableColumnProps<any>[];

    columns.push({
      title: 'Roles',
      dataIndex: APITokenUserDTO.roles,
      renderDataTransform: (value: string[]) => {
        if(value)
        {
            return StringUtil.newLineFormat(value, true);   
        }
      },
      filterType: FilterType.DropdownMulti,
      width: 350,
      dropdownFilterOptions: this.state.roles
    });
    return columns;
  };

  private openAPITokenUser = (record: APITokenUserDTO) => {
    if (record.id) {
        HistoryUtil.push(Routes.generate(Routes.API_TOKEN_USER_DETAILS, { id: record.id }, {}));       
      }
  }

  private addAPITokenUser = () => {
    this.setState({ addAPITokenUser: true });
  }

  private submitAddAPITokenUser = () => {
    this._addAPITokenUserFormRef.current?.submit();
  }

  private cancelAddAPITokenUser = () => {
    this.setState({ addAPITokenUser: false });
    this._addAPITokenUserFormRef.current?.reset();
  }

  private apiTokenUserAdded = () => {
    this.setState({ addAPITokenUser: false });
    this._dataTable?.refresh();
  }

  render() {
    return (
      <>
        {this.renderAddAPITokenUserModal()}
        {this.renderDataTable()}
      </>
    );
  }

  renderAddAPITokenUserModal() {
    return (
      <Modal
        visible={this.state.addAPITokenUser}
        title='Add User'
        okText='Add'
        onOk={this.submitAddAPITokenUser}
        onCancel={this.cancelAddAPITokenUser}>
        <AddAPITokenUserForm
          ref={this._addAPITokenUserFormRef}
          onSuccess={this.apiTokenUserAdded} />
      </Modal>
    );
  }

  renderDataTable() {
    const actionButtons = [];

    actionButtons.push(DataTableButtonUtil.Reset());
    if (AuthorizationUtil.isAuthorized([FeatureFlag.MANAGE_API_TOKEN_USER])) {
      actionButtons.push(DataTableButtonUtil.Default('Add User', () => this.addAPITokenUser()));
    }

    return (
      <>
        {this.renderErrors()}
        <DataTable ref={(element: any) => (this._dataTable = element)}
          onRowClick={this.openAPITokenUser}
          serverSide={true}
          tableProps={{
            rowKey: 'id',
            scroll: { x: 500 }
          }}
          globalSearch={true}
          buttonBar={actionButtons}
          columns={this.getColumnDefinitions()}
          fetchData={{
            fetch: (request: TableRequestDTO) => APITokenUsersApiService.getAPITokenUsersList(request)
          }}
          stateSaving={{ enabled: true, tableUniqueKey: 'api_token_users_list' }} />
      </>
    );
  }

  renderErrors = () => {
    if (this.state.errors && this.state.errors.length > 0) {
      return <Alert type="error" message='Error' showIcon={true} description={this.state.errors.map((x: any) => <>{x} <br /></>)} />;
    }
  }
}

export default APITokenUsersDataTable;
